import moment from "moment";

import { BTSelectItem, type IBaseEntity, type IIsSelectable } from "types/apiResponse/apiResponse";
import { type CalendarItemType } from "types/enum";

import {
    type IGridRequestData,
    type IListRowData,
    ListEntity,
    ListMetadata,
} from "utilities/list/list.types";

import { type BTCalendarView } from "commonComponents/btWrappers/BTCalendar/BTCalendar.types";

import { type IScheduleItemPopoverDisplay } from "entity/schedule/Schedule/common/ScheduleItemPopover/ScheduleItemPopover.types";
import { type IAssignedToData } from "entity/schedule/Schedule/Schedule.api.types";
import { WorkdayExceptionEntry } from "entity/schedule/WorkdayExceptionList/WorkdayExceptionList.api.types";

import { type default as CalendarListExample } from "./CalendarList.api.json";

export type CalendarListFormActions = "newCalendar" | "checkedAction" | "drag" | undefined;

export class CalendarListEntity extends ListEntity {
    constructor(data: typeof CalendarListExample) {
        super(data);
        this.data = data.calendarItems?.map((item: any) => new CalendarEntry(item));
        this.isOffline = data.isOffline;
        this.isBaselineSet = data.isBaselineSet;
        this.canSetBaseline = data.canSetBaseline;
        this.canMarkComplete = data.canMarkComplete;
        this.workdayExceptions = data.workdayExceptions.map(
            (wde: any) => new WorkdayExceptionEntry(wde),
        );
        this.jobs = data.jobs.map((j: any) => new JobEntry(j));
        this.builders = data.builders.map((b: any) => new BuilderEntry(b));
        this.selectedJobsWorkdays = data.selectedJobsWorkdays;
        this.startDate = moment(data.startDate);
        this.exportHeader = data.exportHeader ? new ExportHeader(data.exportHeader) : undefined;
        this.showJobNames = data.showJobNames;
        this.showTimestamps = data.showTimestamps;
        this.listMetadata = new ListMetadata(data.listMetadata);

        const overflowLabels: Record<string, number> | null = (data as any).excludedCounts;
        if (overflowLabels) {
            Object.keys(overflowLabels).forEach((d) => {
                if (overflowLabels[d] > 0) {
                    this.data.push(
                        new CalendarEntry({
                            id: "",
                            title: "",
                            startDate: d,
                            endDate: d,
                            overflowLabel: overflowLabels[d],
                        } as any),
                    );
                }
            });
        }
    }
    data: CalendarEntry[];
    workdayExceptions: WorkdayExceptionEntry[];
    jobs: JobEntry[];
    builders: BuilderEntry[];
    selectedJobsWorkdays: JobWorkDays;
    isOffline: boolean;
    isBaselineSet: boolean;
    canSetBaseline: boolean;
    canMarkComplete: boolean;
    startDate: moment.Moment;
    exportHeader?: ExportHeader;
    showJobNames: boolean;
    showTimestamps: boolean;
    listMetadata: ListMetadata;
}

export class CalendarEntry
    implements IBaseEntity, IListRowData, IIsSelectable, IScheduleItemPopoverDisplay
{
    constructor(data: (typeof CalendarListExample.calendarItems)[0]) {
        // strip all non-numeric characters. td-123, etc.
        this.id = parseInt(data.id.replace(/\D/g, ""));
        // Routable secondary identifier (Guid string) populated for entities that key
        // detail routes on external id rather than numeric id — Tasks today, candidate for others.
        this.externalId = data.externalId ?? undefined;
        this.jobId = data.jobId;
        this.type = data.type;
        this.extraType = data.extraType;
        this.title = data.title;
        this.jobTitle = data.jobTitle ?? "";
        this.jobColor = data.jobColor ?? "";
        const startDate = moment(data.startDate);
        // non-schedule items don't have end dates, and are always 1 day
        const endDate = moment(data.endDate).isSameOrBefore(moment(data.startDate))
            ? moment(data.startDate).endOf("day")
            : moment(data.endDate);
        this.start = startDate.toDate();
        this.actualStart = startDate.toDate();
        this.end = endDate.toDate();
        this.actualEnd = endDate.toDate();
        this.contentWidthInCells = 1;
        this.height = 1;

        this.completed = data.completed;
        this.color = data.color ?? "";
        this.duration = data.duration;
        this.isPredecessor = data.isPredecessor;
        this.hasPredecessor = data.hasPredecessor;
        this.isAllDay = data.isAllDay;
        this.hasHourlyStart = data.hasHourlyStart;
        this.hasHourlyEnd = data.hasHourlyEnd;
        this.phaseId = data.phaseId;
        this.assignees =
            data.assignedUsers?.map((user) => new BTSelectItem<IAssignedToData>(user)) ?? [];
        this.serviceId = data.serviceId ?? undefined;
        this.status = data.status ?? "";
        this.showTime = data.showTime ?? false;
        this.sortId = this.serviceId || this.id;
        this.uid = this.sortId.toString();

        this.overflowLabel = (data as any).overflowLabel ?? 0;
        this.canOpenLead = data.canOpenLead;
        this.contactName = data.contactName;
    }
    // IBaseEntity
    id: number;
    externalId?: string;
    jobId: number;
    builderId: number;

    // IIsSelectable
    isSelected: boolean;

    // Calendar item
    type: CalendarItemType;
    title: string;
    jobTitle: string;
    jobColor: string;
    start: Date;
    end: Date;
    completed: boolean;
    color: string;
    duration: number;
    isPredecessor: boolean;
    hasPredecessor: boolean;
    isAllDay: boolean;
    phaseId: number;
    assignees: BTSelectItem<IAssignedToData>[];

    // Warranty Service
    serviceId?: number;

    // ScheduleItemPopoverDisplay
    hasHourlyStart: boolean;
    hasHourlyEnd: boolean;
    status?: string;
    showTime?: boolean;

    // Used for sorting on the month/week/day view
    sortId: number;
    uid: string;
    order?: number;
    actualStart: Date;
    actualEnd: Date;

    // Used for word-wrap
    contentWidthInCells: number;
    height: number;

    // Used on agenda widget
    overflowLabel: number;

    // used on activity calendar
    canOpenLead?: boolean;
    contactName?: string;
    extraType?: number;
}

export class JobEntry {
    constructor(data: (typeof CalendarListExample.jobs)[0]) {
        this.id = data.id;
        this.builderId = data.builderId;
        this.name = data.name;
        this.workdays = data.workdays;
        this.isOffline = data.calendarOffline;
        this.color = data.color;
    }
    id: number;
    builderId: number;
    name: string;
    workdays: JobWorkDays;
    isOffline: boolean;
    color: string;
}

export class BuilderEntry {
    constructor(data: any) {
        this.id = data.id;
        this.name = data.name;
    }
    id: number;
    name: string;
}

export class ExportHeader {
    constructor(data: any) {
        this.logoPath = data.logoPath;
        this.address = data.address;
        this.phone = data.phone;
    }

    logoPath: string;
    address: string;
    phone: string;
}

export class JobWorkDays {
    sunday: boolean;
    monday: boolean;
    tuesday: boolean;
    wednesday: boolean;
    thursday: boolean;
    friday: boolean;
    saturday: boolean;
}

export interface ICalendarListRequestData extends IGridRequestData {
    startDate?: moment.Moment;
    view: BTCalendarView;
    pagesLoaded: number;
    loadOtherItems: boolean;
    currentWeekOnly: boolean;
    perDayLimit: number;
}

export interface IWorkdayInfo {
    listEntity: CalendarListEntity | undefined;
    workdayExceptions: WorkdayExceptionEntry[];
    jobWorkdays: JobWorkDays;
    jobIds: number[];
}

export function getWdeInfo(
    listEntity: CalendarListEntity | undefined,
    jobIds: number[],
): IWorkdayInfo {
    let workdayExceptions: WorkdayExceptionEntry[] = [];
    const jobWorkdays: JobWorkDays = listEntity?.selectedJobsWorkdays ?? {
        sunday: true,
        monday: true,
        tuesday: true,
        wednesday: true,
        thursday: true,
        friday: true,
        saturday: true,
    };
    if (listEntity !== undefined) {
        if (jobIds.length === 1) {
            workdayExceptions = listEntity.workdayExceptions;
        } else {
            workdayExceptions = listEntity.workdayExceptions.filter((wde) => wde.allJobs);
        }
    }
    return { listEntity, workdayExceptions, jobWorkdays, jobIds };
}
